import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { AppLoadingService } from '@examdojo/app-state';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  race,
  startWith,
  switchMap,
  take,
  takeWhile,
  timer,
} from 'rxjs';

const LOADING_BAR_DELAY_MS = 500;
const LOADING_BAR_MIN_DURATION_MS = 2000;

@Injectable({ providedIn: 'root' })
export class RoutingLoadingService {
  constructor(
    private readonly router: Router,
    private readonly appLoadingService: AppLoadingService,
  ) {}

  readonly isRoutingLoading$ = this.router.events.pipe(
    map((event) => {
      if (event instanceof NavigationStart) {
        return true;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        return false;
      }

      return null;
    }),
    filter((isLoading) => isLoading !== null),
    startWith(true),
  );

  readonly isInitialRoutingLoading$ = this.isRoutingLoading$.pipe(
    takeWhile(
      Boolean,
      // Include the first `false` value
      true,
    ),
  );

  readonly showFullScreenLoader$ = combineLatest([
    this.appLoadingService.isLoading$,
    this.isInitialRoutingLoading$,
  ]).pipe(map(([appLoading, routingLoading]) => appLoading || routingLoading));

  readonly showLoadingBar$ = this.isRoutingLoading$.pipe(
    distinctUntilChanged(),
    filter(Boolean),
    switchMap(() =>
      race([
        // Loading finishes before delay - don't show
        this.isRoutingLoading$.pipe(
          filter((loading) => !loading),
          take(1),
          map(() => false),
        ),
        // Loading continues past delay - show with minimum duration
        timer(LOADING_BAR_DELAY_MS).pipe(
          switchMap(() =>
            forkJoin([
              this.isRoutingLoading$.pipe(
                filter((loading) => !loading),
                take(1),
              ),
              timer(LOADING_BAR_MIN_DURATION_MS),
            ]).pipe(
              map(() => false),
              startWith(true),
            ),
          ),
        ),
      ]),
    ),
    startWith(false),
    distinctUntilChanged(),
  );
}
